import { Fragment, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ehUuidAceito } from '@malgoos/shared';
import { Cartao } from '../../componentes/base';
import { Estado, Vazio } from '../../componentes/estados';
import { Selecao } from '../../componentes/formulario';
import { get, getTodos } from '../../nucleo/api';
import { data, dinheiro, textoLivre } from '../../nucleo/formato';
import { Barra, BotaoExportar, Kpi, LinhaEscopo, Rolagem, Segmentado, TD, TH, useParametro, usePeriodo, Valor } from './comum';
import { Detalhe } from './Detalhe';
import type { Fluxo as FluxoApi, FluxoCategorias, NoCategoriaFluxo } from './tipos';

type Agrupar = 'dia' | 'semana' | 'mes';

/** Contas para o filtro (a lista do Financeiro, pela API; nunca pela tela de outro módulo). */
function useContasFinanceiro() {
  return useQuery({ queryKey: ['/financeiro/contas', 'relatorios'], queryFn: () => getTodos<{ id: string; nome: string; moeda: string }>('/financeiro/contas'), staleTime: 30_000 });
}

function NosCategoria({ nos, nivel, moeda, aoAbrir }: { nos: NoCategoriaFluxo[]; nivel: number; moeda: string; aoAbrir: (n: NoCategoriaFluxo) => void }) {
  const { t } = useTranslation('relatorios');
  return (
    <>
      {nos.map((n) => {
        const nome = n.categoria_id ? `${n.codigo ? `${n.codigo} ` : ''}${n.nome ?? ''}` : t('comum.semCategoria');
        return (
          <Fragment key={n.categoria_id ?? 'sem'}>
            <tr className="border-b border-borda last:border-0">
              <th scope="row" className={`${TD} text-left font-normal`} style={{ paddingLeft: `${1 + nivel * 1.25}rem` }}>
                <button type="button" onClick={() => aoAbrir(n)} aria-label={t('comum.detalhar', { nome })} className="text-left break-words underline-offset-2 hover:text-acento-forte hover:underline">
                  {nome}
                </button>
              </th>
              <td className={`${TD} text-right text-verde`}>
                <Valor centavos={n.entradas_centavos} moeda={moeda} />
              </td>
              <td className={`${TD} text-right`}>
                <Valor centavos={-n.saidas_centavos} moeda={moeda} />
              </td>
              <td className={`${TD} text-right font-medium`}>
                <Valor centavos={n.resultado_centavos} moeda={moeda} />
              </td>
            </tr>
            {n.filhos.length > 0 && <NosCategoria nos={n.filhos} nivel={nivel + 1} moeda={moeda} aoAbrir={aoAbrir} />}
          </Fragment>
        );
      })}
    </>
  );
}

function PorCategoria({ de, ate, aoAbrir }: { de: string; ate: string; aoAbrir: (titulo: string, params: Record<string, unknown>) => void }) {
  const { t } = useTranslation('relatorios');
  const params = { de, ate };
  const q = useQuery({ queryKey: ['/relatorios/fluxo/categorias', params], queryFn: () => get<FluxoCategorias>('/relatorios/fluxo/categorias', params), placeholderData: keepPreviousData });
  return (
    <Estado consulta={q}>
      {(d) =>
        d.por_moeda.length === 0 ? (
          <Cartao>
            <Vazio titulo={t('fluxo.vazioTitulo')} texto={t('fluxo.vazioTexto')} />
          </Cartao>
        ) : (
          <>
            {d.por_moeda.map((m) => (
              <Cartao key={m.moeda} titulo={d.por_moeda.length > 1 ? t('comum.moeda', { moeda: m.moeda }) : t('fluxo.porCategoria')} semPadding>
                <Rolagem rotulo={`${t('fluxo.porCategoria')} (${m.moeda})`}>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-borda text-left text-tinta-3">
                        <th scope="col" className={TH}>{t('detalhe.categoria')}</th>
                        <th scope="col" className={`${TH} text-right`}>{t('fluxo.entradas')}</th>
                        <th scope="col" className={`${TH} text-right`}>{t('fluxo.saidas')}</th>
                        <th scope="col" className={`${TH} text-right`}>{t('fluxo.resultado')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <NosCategoria nos={m.arvore} nivel={0} moeda={m.moeda} aoAbrir={(n) => aoAbrir(n.nome ?? t('comum.semCategoria'), { de, ate, categoria_id: n.categoria_id ?? 'sem', moeda: m.moeda })} />
                      <tr className="bg-sup-2 font-semibold">
                        <th scope="row" className={`${TD} text-left`}>{t('comum.total')}</th>
                        <td className={`${TD} text-right`}>
                          <Valor centavos={m.entradas_centavos} moeda={m.moeda} />
                        </td>
                        <td className={`${TD} text-right`}>
                          <Valor centavos={-m.saidas_centavos} moeda={m.moeda} />
                        </td>
                        <td className={`${TD} text-right`}>
                          <Valor centavos={m.resultado_centavos} moeda={m.moeda} />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </Rolagem>
                <p className="border-t border-borda px-4 py-3 text-xs text-tinta-3">{t('fluxo.categoriasAjuda')}</p>
              </Cartao>
            ))}
          </>
        )
      }
    </Estado>
  );
}

/** R1 · Fluxo de caixa por moeda: realizado, previsto (com os títulos), saldo projetado, por conta, por categoria e drill. */
export function Fluxo() {
  const { t } = useTranslation('relatorios');
  const { periodo, seletor } = usePeriodo();
  const [agrupar, setAgrupar] = useParametro<Agrupar>('agrupar', 'dia', ['dia', 'semana', 'mes']);
  const [visao, setVisao] = useParametro<'periodo' | 'categoria'>('visao', 'periodo', ['periodo', 'categoria']);
  const [conta, setConta] = useParametro<string>('conta_id', '', ehUuidAceito);
  const contas = useContasFinanceiro();
  const [aberto, setAberto] = useState<{ titulo: string; params: Record<string, unknown> } | null>(null);
  const params = { de: periodo.de, ate: periodo.ate, agrupar, conta_id: conta || undefined };
  const q = useQuery({ queryKey: ['/relatorios/fluxo', params], queryFn: () => get<FluxoApi>('/relatorios/fluxo', params), placeholderData: keepPreviousData, enabled: visao === 'periodo' });
  const abrir = (titulo: string, p: Record<string, unknown>) => setAberto({ titulo, params: p });

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <Barra>
        {seletor}
        <Segmentado
          rotulo={t('fluxo.visao')}
          valor={visao}
          aoMudar={setVisao}
          opcoes={[
            { valor: 'periodo', rotulo: t('fluxo.porPeriodo') },
            { valor: 'categoria', rotulo: t('fluxo.porCategoria') },
          ]}
        />
        {visao === 'periodo' && (
          <>
            <Segmentado rotulo={t('fluxo.agrupar')} valor={agrupar} aoMudar={setAgrupar} opcoes={(['dia', 'semana', 'mes'] as const).map((x) => ({ valor: x, rotulo: t(`fluxo.por.${x}`) }))} />
            <div className="w-56 max-w-full">
              <Selecao aria-label={t('fluxo.conta')} vazio={t('fluxo.todasContas')} opcoes={(contas.data?.itens ?? []).map((c) => ({ valor: c.id, rotulo: c.nome }))} value={conta} onChange={(e) => setConta(e.target.value)} />
            </div>
          </>
        )}
        <BotaoExportar relatorio="fluxo" parametros={params} />
      </Barra>

      {visao === 'categoria' ? (
        <PorCategoria de={periodo.de} ate={periodo.ate} aoAbrir={abrir} />
      ) : (
        <Estado consulta={q}>
          {(d) => (
            <>
              <LinhaEscopo escopo={d.escopo} />
              {!d.previsto_com_titulos && <p className="rounded-lg border border-borda px-3 py-2 text-sm text-tinta-2">{d.conta_id ? t('fluxo.avisoConta') : t('fluxo.avisoSemTitulos')}</p>}
              {d.por_moeda.length === 0 ? (
                <Cartao>
                  <Vazio titulo={t('fluxo.vazioTitulo')} texto={t('fluxo.vazioTexto')} />
                </Cartao>
              ) : (
                d.por_moeda.map((m) => (
                  <section key={m.moeda} aria-label={t('comum.moeda', { moeda: m.moeda })} className="flex min-w-0 flex-col gap-4">
                    {d.por_moeda.length > 1 && <h2 className="text-sm font-semibold">{t('comum.moeda', { moeda: m.moeda })}</h2>}
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6">
                      <Kpi rotulo={t('fluxo.abertura')} valor={dinheiro(m.abertura_centavos, m.moeda)} />
                      <Kpi rotulo={t('fluxo.entradasRealizadas')} cor="text-verde" valor={dinheiro(m.totais.realizado_entradas_centavos, m.moeda)} />
                      <Kpi rotulo={t('fluxo.saidasRealizadas')} cor="text-perigo" valor={dinheiro(m.totais.realizado_saidas_centavos, m.moeda)} />
                      <Kpi rotulo={t('fluxo.previstoEntradas')} cor="text-ciano" valor={dinheiro(m.totais.previsto_entradas_centavos, m.moeda)} />
                      <Kpi rotulo={t('fluxo.previstoSaidas')} cor="text-laranja" valor={dinheiro(m.totais.previsto_saidas_centavos, m.moeda)} />
                      <Kpi rotulo={t('fluxo.saldoFinal')} cor={m.saldo_final_projetado_centavos < 0 ? 'text-perigo' : 'text-tinta-1'} valor={dinheiro(m.saldo_final_projetado_centavos, m.moeda)} />
                    </div>
                    <Cartao semPadding>
                      <Rolagem rotulo={`${t('comum.tabela')} (${m.moeda})`}>
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-borda text-left text-tinta-3">
                              <th scope="col" className={TH}>{t('fluxo.periodo')}</th>
                              <th scope="col" className={`${TH} text-right`}>{t('fluxo.entradasRealizadas')}</th>
                              <th scope="col" className={`${TH} text-right`}>{t('fluxo.saidasRealizadas')}</th>
                              <th scope="col" className={`${TH} text-right`}>{t('fluxo.previstoEntradas')}</th>
                              <th scope="col" className={`${TH} text-right`}>{t('fluxo.previstoSaidas')}</th>
                              <th scope="col" className={`${TH} text-right`}>{t('fluxo.saldoProjetado')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {m.periodos.map((p) => {
                              const rotulo = agrupar === 'dia' ? data(p.inicio) : t('fluxo.intervalo', { de: data(p.inicio), ate: data(p.fim) });
                              const temRealizado = p.realizado_entradas_centavos || p.realizado_saidas_centavos;
                              return (
                                <tr key={p.inicio} className={`border-b border-borda last:border-0 ${p.inicio <= d.hoje && d.hoje <= p.fim ? 'bg-acento-suave' : ''}`}>
                                  <th scope="row" className={`${TD} numero text-left font-normal whitespace-nowrap`}>
                                    {temRealizado && !d.conta_id ? (
                                      <button type="button" onClick={() => abrir(rotulo, { de: p.inicio, ate: p.fim, moeda: m.moeda })} aria-label={t('comum.detalhar', { nome: rotulo })} className="underline-offset-2 hover:text-acento-forte hover:underline">
                                        {rotulo}
                                      </button>
                                    ) : (
                                      rotulo
                                    )}
                                  </th>
                                  <td className={`${TD} text-right text-verde`}>
                                    <Valor centavos={p.realizado_entradas_centavos} moeda={m.moeda} />
                                  </td>
                                  <td className={`${TD} text-right`}>
                                    <Valor centavos={-p.realizado_saidas_centavos} moeda={m.moeda} />
                                  </td>
                                  <td className={`${TD} text-right text-ciano`}>
                                    <Valor centavos={p.previsto_entradas_centavos} moeda={m.moeda} />
                                  </td>
                                  <td className={`${TD} text-right`}>
                                    <Valor centavos={-p.previsto_saidas_centavos} moeda={m.moeda} />
                                  </td>
                                  <td className={`${TD} text-right font-medium`}>
                                    <Valor centavos={p.saldo_projetado_centavos} moeda={m.moeda} />
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </Rolagem>
                    </Cartao>
                  </section>
                ))
              )}
            </>
          )}
        </Estado>
      )}
      {aberto && <Detalhe titulo={textoLivre(aberto.titulo)} caminho="/relatorios/fluxo/detalhe" params={aberto.params} aoFechar={() => setAberto(null)} />}
    </div>
  );
}
